// Primary Mathematics Curriculum - Years 1-6
// Based on Vanuatu National Curriculum - Numeracy/Mathematics
// 4 Strands: Number & Operations, Measurement, Geometry, Statistics & Probability

// ============================================
// YEAR 1 MATHEMATICS CURRICULUM
// Foundation Numeracy
// ============================================
export const mathYear1 = {
  year: 1,
  subject: 'Mathematics',
  totalWeeks: 40,
  periodsPerWeek: 5,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Numbers 1-20 & Counting',
      scheme: [
        {
          week: 1,
          topic: 'Counting to 10',
          strand: 'Number & Operations',
          learningOutcomes: [
            'Count objects from 1 to 10',
            'Recognize numerals 1-10',
            'Match quantities to numerals'
          ],
          activities: [
            'Counting songs',
            'Object counting activities',
            'Number matching games'
          ],
          resources: ['Counters', 'Number cards', 'Counting books'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Welcome to Numbers!',
              duration: '30 min',
              objectives: ['Count objects 1-5', 'Say number names'],
              introduction: 'Numbers are everywhere! Let\'s learn to count.',
              mainActivities: [
                { activity: 'Counting Song', duration: '10 min', description: 'Sing counting song 1-5 with actions' },
                { activity: 'Object Counting', duration: '15 min', description: 'Count classroom objects together' },
                { activity: 'Number Show', duration: '5 min', description: 'Show fingers for numbers 1-5' }
              ],
              materials: ['Counters', 'Classroom objects'],
              assessment: 'Can count to 5',
              homework: 'Count 5 things at home'
            },
            {
              day: 'Tuesday',
              title: 'Numbers 1-5',
              duration: '30 min',
              objectives: ['Recognize numerals 1-5', 'Match numeral to quantity'],
              introduction: 'Numbers have special shapes. Let\'s learn them!',
              mainActivities: [
                { activity: 'Numeral Introduction', duration: '10 min', description: 'Show and trace numerals 1-5' },
                { activity: 'Matching Game', duration: '15 min', description: 'Match numerals to groups of objects' },
                { activity: 'Number Hunt', duration: '5 min', description: 'Find numerals in classroom' }
              ],
              materials: ['Number cards', 'Matching materials'],
              assessment: 'Recognizes numerals 1-5',
              homework: 'Find numbers at home'
            },
            {
              day: 'Wednesday',
              title: 'Counting to 10',
              duration: '30 min',
              objectives: ['Count objects 1-10', 'Continue counting sequence'],
              introduction: 'Let\'s count even higher - all the way to 10!',
              mainActivities: [
                { activity: 'Counting Extension', duration: '10 min', description: 'Count 6-10 with song' },
                { activity: 'Ten Frame Fun', duration: '15 min', description: 'Use ten frames to count objects' },
                { activity: 'Counting Practice', duration: '5 min', description: 'Partner counting practice' }
              ],
              materials: ['Ten frames', 'Counters'],
              assessment: 'Can count to 10',
              homework: 'Count to 10 with family'
            },
            {
              day: 'Thursday',
              title: 'Numerals 6-10',
              duration: '30 min',
              objectives: ['Recognize numerals 6-10', 'Write numerals'],
              introduction: 'More number shapes to learn!',
              mainActivities: [
                { activity: 'Numeral Learning', duration: '10 min', description: 'Introduce numerals 6-10' },
                { activity: 'Tracing Practice', duration: '15 min', description: 'Trace and write numerals' },
                { activity: 'Number Game', duration: '5 min', description: 'Number recognition game' }
              ],
              materials: ['Writing sheets', 'Sand trays'],
              assessment: 'Writes numerals 1-10',
              homework: 'Practice writing numbers'
            },
            {
              day: 'Friday',
              title: 'Counting Review',
              duration: '30 min',
              objectives: ['Count and recognize 1-10', 'Demonstrate learning'],
              introduction: 'Let\'s show what we learned about counting!',
              mainActivities: [
                { activity: 'Counting Games', duration: '15 min', description: 'Fun counting activities' },
                { activity: 'Number Show', duration: '10 min', description: 'Students demonstrate counting' },
                { activity: 'Celebration', duration: '5 min', description: 'Celebrate counting success!' }
              ],
              materials: ['Games', 'Rewards'],
              assessment: 'Counts 1-10 accurately',
              homework: 'Practice counting over weekend'
            }
          ]
        },
        {
          week: 2,
          topic: 'Comparing Numbers (More/Less)',
          strand: 'Number & Operations',
          learningOutcomes: [
            'Compare groups using more and less',
            'Use vocabulary: more, less, same',
            'Compare numbers to 10'
          ],
          activities: ['Comparison activities', 'Sorting games', 'More/less practice'],
          resources: ['Counters', 'Comparison cards', 'Sorting materials']
        },
        {
          week: 3,
          topic: 'Numbers 11-20',
          strand: 'Number & Operations',
          learningOutcomes: [
            'Count objects from 11 to 20',
            'Recognize teen numbers',
            'Understand teen numbers as 10 and some more'
          ],
          activities: ['Teen number songs', 'Place value introduction', 'Counting to 20'],
          resources: ['Ten frames', 'Place value materials', 'Number line']
        },
        {
          week: 4,
          topic: 'Basic Shapes',
          strand: 'Geometry',
          learningOutcomes: [
            'Identify circles, squares, triangles, rectangles',
            'Name basic shapes',
            'Find shapes in environment'
          ],
          activities: ['Shape hunts', 'Shape sorting', 'Shape art'],
          resources: ['Shape blocks', 'Shape cards', 'Art supplies']
        },
        {
          week: 5,
          topic: 'Addition Introduction (Within 5)',
          strand: 'Number & Operations',
          learningOutcomes: [
            'Understand addition as putting together',
            'Add numbers within 5',
            'Use objects to add'
          ],
          activities: ['Joining activities', 'Addition stories', 'Hands-on adding'],
          resources: ['Counters', 'Addition cards', 'Story problems']
        },
        {
          week: 6,
          topic: 'Length Comparison',
          strand: 'Measurement',
          learningOutcomes: [
            'Compare lengths using longer/shorter',
            'Order objects by length',
            'Use non-standard units to measure'
          ],
          activities: ['Length comparison', 'Ordering activities', 'Measuring with objects'],
          resources: ['Objects of varying lengths', 'Non-standard units', 'Comparison charts']
        },
        {
          week: 7,
          topic: 'Addition Within 10',
          strand: 'Number & Operations',
          learningOutcomes: [
            'Add numbers with sums to 10',
            'Use pictures and objects to add',
            'Write addition sentences'
          ],
          activities: ['Addition practice', 'Picture addition', 'Writing number sentences'],
          resources: ['Addition worksheets', 'Ten frames', 'Number sentences']
        },
        {
          week: 8,
          topic: 'Position Words',
          strand: 'Geometry',
          learningOutcomes: [
            'Use position words: on, under, beside, in front, behind',
            'Follow position instructions',
            'Describe positions of objects'
          ],
          activities: ['Position games', 'Following directions', 'Describing positions'],
          resources: ['Position cards', 'Objects', 'Game materials']
        },
        {
          week: 9,
          topic: 'Subtraction Introduction',
          strand: 'Number & Operations',
          learningOutcomes: [
            'Understand subtraction as taking away',
            'Subtract within 5',
            'Use objects to subtract'
          ],
          activities: ['Taking away activities', 'Subtraction stories', 'Hands-on subtracting'],
          resources: ['Counters', 'Subtraction cards', 'Story problems']
        },
        {
          week: 10,
          topic: 'Term 1 Review & Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Demonstrate counting skills',
            'Show addition/subtraction understanding',
            'Identify shapes and positions'
          ],
          activities: ['Skills review', 'Assessment activities', 'Celebration'],
          resources: ['Assessment materials', 'Review games', 'Rewards']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Operations & Measurement',
      scheme: [
        {
          week: 1,
          topic: 'Addition Review & Practice',
          strand: 'Number & Operations',
          learningOutcomes: ['Review addition to 10', 'Build addition fluency', 'Solve word problems'],
          activities: ['Addition games', 'Fluency practice', 'Word problems'],
          resources: ['Addition cards', 'Games', 'Problem cards']
        },
        {
          week: 2,
          topic: 'Subtraction Within 10',
          strand: 'Number & Operations',
          learningOutcomes: ['Subtract numbers within 10', 'Use objects and drawings', 'Write subtraction sentences'],
          activities: ['Subtraction practice', 'Drawing subtraction', 'Number sentences'],
          resources: ['Subtraction materials', 'Worksheets', 'Counters']
        },
        {
          week: 3,
          topic: 'Patterns',
          strand: 'Number & Operations',
          learningOutcomes: ['Identify simple patterns', 'Continue patterns', 'Create own patterns'],
          activities: ['Pattern recognition', 'Pattern continuation', 'Pattern creation'],
          resources: ['Pattern blocks', 'Pattern cards', 'Art supplies']
        },
        {
          week: 4,
          topic: 'Time Concepts',
          strand: 'Measurement',
          learningOutcomes: ['Understand morning, afternoon, night', 'Sequence daily events', 'Read o\'clock time'],
          activities: ['Daily routine', 'Time sorting', 'Clock introduction'],
          resources: ['Clock', 'Daily routine cards', 'Time worksheets']
        },
        {
          week: 5,
          topic: 'Addition and Subtraction Together',
          strand: 'Number & Operations',
          learningOutcomes: ['Relate addition and subtraction', 'Solve mixed problems', 'Use fact families'],
          activities: ['Related facts', 'Mixed practice', 'Fact family introduction'],
          resources: ['Fact family cards', 'Mixed problems', 'Counters']
        },
        {
          week: 6,
          topic: 'Weight Comparison',
          strand: 'Measurement',
          learningOutcomes: ['Compare weights: heavier/lighter', 'Use balance scales', 'Order by weight'],
          activities: ['Weight comparisons', 'Balance activities', 'Weight ordering'],
          resources: ['Balance scales', 'Objects', 'Weight cards']
        },
        {
          week: 7,
          topic: 'Numbers to 30',
          strand: 'Number & Operations',
          learningOutcomes: ['Count to 30', 'Read and write numbers to 30', 'Understand place value'],
          activities: ['Extended counting', 'Number writing', 'Place value practice'],
          resources: ['Number line', 'Place value materials', 'Worksheets']
        },
        {
          week: 8,
          topic: '3D Shapes',
          strand: 'Geometry',
          learningOutcomes: ['Identify sphere, cube, cylinder, cone', 'Sort 3D shapes', 'Find 3D shapes in environment'],
          activities: ['3D shape exploration', 'Shape sorting', 'Shape hunt'],
          resources: ['3D shape models', 'Sorting materials', 'Real-world objects']
        },
        {
          week: 9,
          topic: 'Problem Solving Strategies',
          strand: 'Number & Operations',
          learningOutcomes: ['Use drawings to solve problems', 'Act out problems', 'Check answers'],
          activities: ['Drawing solutions', 'Acting out', 'Checking work'],
          resources: ['Problem cards', 'Drawing materials', 'Counters']
        },
        {
          week: 10,
          topic: 'Term 2 Review & Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show operations mastery', 'Demonstrate measurement skills', 'Apply problem solving'],
          activities: ['Skills assessment', 'Review activities', 'Portfolio review'],
          resources: ['Assessment materials', 'Review games', 'Portfolios']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Place Value & Data',
      scheme: [
        {
          week: 1,
          topic: 'Numbers to 50',
          strand: 'Number & Operations',
          learningOutcomes: ['Count to 50', 'Read and write numbers to 50', 'Identify tens and ones'],
          activities: ['Counting practice', 'Place value activities', 'Number writing'],
          resources: ['Number chart', 'Base ten blocks', 'Worksheets']
        },
        {
          week: 2,
          topic: 'Skip Counting by 2s and 5s',
          strand: 'Number & Operations',
          learningOutcomes: ['Skip count by 2s', 'Skip count by 5s', 'Use skip counting patterns'],
          activities: ['Skip counting songs', 'Pattern practice', 'Number line jumps'],
          resources: ['Number lines', 'Skip counting charts', 'Manipulatives']
        },
        {
          week: 3,
          topic: 'Addition to 20',
          strand: 'Number & Operations',
          learningOutcomes: ['Add with sums to 20', 'Use counting on strategy', 'Build fact fluency'],
          activities: ['Addition practice', 'Strategy work', 'Fluency games'],
          resources: ['Ten frames', 'Addition cards', 'Games']
        },
        {
          week: 4,
          topic: 'Collecting Data',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Collect simple data', 'Organize data in groups', 'Count and compare data'],
          activities: ['Data collection', 'Sorting activities', 'Counting results'],
          resources: ['Survey materials', 'Sorting mats', 'Recording sheets']
        },
        {
          week: 5,
          topic: 'Subtraction to 20',
          strand: 'Number & Operations',
          learningOutcomes: ['Subtract within 20', 'Use counting back strategy', 'Relate to addition'],
          activities: ['Subtraction practice', 'Strategy work', 'Related facts'],
          resources: ['Number lines', 'Subtraction cards', 'Counters']
        },
        {
          week: 6,
          topic: 'Capacity Comparison',
          strand: 'Measurement',
          learningOutcomes: ['Compare capacities: more/less', 'Use non-standard units', 'Order by capacity'],
          activities: ['Capacity comparison', 'Pouring activities', 'Ordering'],
          resources: ['Containers', 'Water/sand', 'Measuring cups']
        },
        {
          week: 7,
          topic: 'Making Graphs',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Create picture graphs', 'Read simple graphs', 'Answer questions about graphs'],
          activities: ['Graph making', 'Graph reading', 'Data questions'],
          resources: ['Graph paper', 'Picture stickers', 'Data sets']
        },
        {
          week: 8,
          topic: 'Symmetry',
          strand: 'Geometry',
          learningOutcomes: ['Identify lines of symmetry', 'Create symmetrical designs', 'Find symmetry in nature'],
          activities: ['Symmetry exploration', 'Art activities', 'Nature walk'],
          resources: ['Mirrors', 'Paper folding', 'Symmetry pictures']
        },
        {
          week: 9,
          topic: 'Numbers to 100',
          strand: 'Number & Operations',
          learningOutcomes: ['Count to 100', 'Understand tens and ones to 100', 'Use hundred chart'],
          activities: ['Extended counting', 'Place value work', 'Hundred chart activities'],
          resources: ['Hundred chart', 'Base ten blocks', 'Worksheets']
        },
        {
          week: 10,
          topic: 'Term 3 Review & Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate place value understanding', 'Show data skills', 'Apply all operations'],
          activities: ['Skills review', 'Assessment activities', 'Progress celebration'],
          resources: ['Assessment materials', 'Review activities', 'Certificates']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Consolidation & Application',
      scheme: [
        {
          week: 1,
          topic: 'Money Introduction',
          strand: 'Number & Operations',
          learningOutcomes: ['Identify Vanuatu coins', 'Count coins', 'Understand coin values'],
          activities: ['Coin recognition', 'Coin counting', 'Shop play'],
          resources: ['Play money', 'Coin pictures', 'Shop materials']
        },
        {
          week: 2,
          topic: 'Addition & Subtraction Fluency',
          strand: 'Number & Operations',
          learningOutcomes: ['Build fact fluency to 10', 'Recall basic facts', 'Solve problems quickly'],
          activities: ['Fluency practice', 'Flash cards', 'Timed activities'],
          resources: ['Flash cards', 'Games', 'Worksheets']
        },
        {
          week: 3,
          topic: 'Length Measurement',
          strand: 'Measurement',
          learningOutcomes: ['Measure using non-standard units', 'Compare measurements', 'Estimate lengths'],
          activities: ['Measuring activities', 'Estimation practice', 'Recording measurements'],
          resources: ['Non-standard units', 'Measuring tools', 'Recording sheets']
        },
        {
          week: 4,
          topic: 'Shape Patterns',
          strand: 'Geometry',
          learningOutcomes: ['Create shape patterns', 'Extend shape patterns', 'Describe patterns'],
          activities: ['Pattern building', 'Pattern extension', 'Pattern description'],
          resources: ['Pattern blocks', 'Shape cards', 'Recording materials']
        },
        {
          week: 5,
          topic: 'Word Problems',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand word problems', 'Choose operations', 'Solve and explain'],
          activities: ['Problem solving', 'Strategy selection', 'Explanation practice'],
          resources: ['Word problem cards', 'Manipulatives', 'Recording sheets']
        },
        {
          week: 6,
          topic: 'Ordinal Numbers',
          strand: 'Number & Operations',
          learningOutcomes: ['Use ordinal numbers (1st-10th)', 'Describe positions', 'Follow ordinal instructions'],
          activities: ['Ordinal practice', 'Position games', 'Following directions'],
          resources: ['Ordinal cards', 'Objects', 'Game materials']
        },
        {
          week: 7,
          topic: 'Calendar and Days',
          strand: 'Measurement',
          learningOutcomes: ['Know days of the week', 'Use calendar', 'Sequence events by day'],
          activities: ['Calendar activities', 'Day sequencing', 'Weekly planning'],
          resources: ['Calendar', 'Day cards', 'Sequencing materials']
        },
        {
          week: 8,
          topic: 'Review: Numbers & Operations',
          strand: 'Number & Operations',
          learningOutcomes: ['Review all number concepts', 'Show counting mastery', 'Demonstrate operation skills'],
          activities: ['Number review', 'Operation practice', 'Skills demonstration'],
          resources: ['Review materials', 'Games', 'Assessment tools']
        },
        {
          week: 9,
          topic: 'Review: Measurement & Geometry',
          strand: 'Measurement/Geometry',
          learningOutcomes: ['Review measurement concepts', 'Review geometry skills', 'Apply learning'],
          activities: ['Measurement review', 'Shape review', 'Application activities'],
          resources: ['Review materials', 'Shapes', 'Measuring tools']
        },
        {
          week: 10,
          topic: 'Year 1 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Complete final assessment', 'Celebrate Year 1 math learning', 'Prepare for Year 2'],
          activities: ['Final assessment', 'Awards', 'Celebration'],
          resources: ['Assessment materials', 'Certificates', 'Celebration supplies']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 2 MATHEMATICS CURRICULUM
// Building Number Sense
// ============================================
export const mathYear2 = {
  year: 2,
  subject: 'Mathematics',
  totalWeeks: 40,
  periodsPerWeek: 5,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Place Value & Addition Strategies',
      scheme: [
        {
          week: 1,
          topic: 'Numbers to 100 Review',
          strand: 'Number & Operations',
          learningOutcomes: ['Read and write numbers to 100', 'Understand tens and ones', 'Compare two-digit numbers'],
          activities: ['Number reading', 'Place value activities', 'Number comparison'],
          resources: ['Hundred chart', 'Base ten blocks', 'Comparison cards']
        },
        {
          week: 2,
          topic: 'Addition Strategies',
          strand: 'Number & Operations',
          learningOutcomes: ['Use counting on', 'Use doubles', 'Use make ten strategy'],
          activities: ['Strategy practice', 'Doubles games', 'Make ten activities'],
          resources: ['Strategy cards', 'Ten frames', 'Addition games']
        },
        {
          week: 3,
          topic: 'Addition Within 20',
          strand: 'Number & Operations',
          learningOutcomes: ['Add fluently within 20', 'Apply strategies', 'Solve word problems'],
          activities: ['Fluency practice', 'Problem solving', 'Strategy application'],
          resources: ['Flash cards', 'Word problems', 'Manipulatives']
        },
        {
          week: 4,
          topic: 'Measuring Length',
          strand: 'Measurement',
          learningOutcomes: ['Measure in centimeters', 'Use rulers correctly', 'Estimate and measure'],
          activities: ['Ruler practice', 'Measurement activities', 'Estimation'],
          resources: ['Rulers', 'Objects to measure', 'Recording sheets']
        },
        {
          week: 5,
          topic: 'Subtraction Strategies',
          strand: 'Number & Operations',
          learningOutcomes: ['Use counting back', 'Use related facts', 'Use subtraction patterns'],
          activities: ['Strategy instruction', 'Pattern recognition', 'Related fact practice'],
          resources: ['Strategy cards', 'Number lines', 'Subtraction games']
        },
        {
          week: 6,
          topic: 'Subtraction Within 20',
          strand: 'Number & Operations',
          learningOutcomes: ['Subtract fluently within 20', 'Apply strategies', 'Solve word problems'],
          activities: ['Fluency building', 'Word problems', 'Mixed practice'],
          resources: ['Flash cards', 'Problem cards', 'Worksheets']
        },
        {
          week: 7,
          topic: '2D Shapes and Properties',
          strand: 'Geometry',
          learningOutcomes: ['Identify 2D shape properties', 'Count sides and corners', 'Sort shapes by properties'],
          activities: ['Property exploration', 'Sorting activities', 'Shape art'],
          resources: ['Shape cards', 'Sorting materials', 'Art supplies']
        },
        {
          week: 8,
          topic: 'Odd and Even Numbers',
          strand: 'Number & Operations',
          learningOutcomes: ['Identify odd and even numbers', 'Understand odd/even patterns', 'Sort numbers'],
          activities: ['Pattern discovery', 'Sorting activities', 'Pairing practice'],
          resources: ['Number cards', 'Counters', 'Sorting mats']
        },
        {
          week: 9,
          topic: 'Time to Half Hour',
          strand: 'Measurement',
          learningOutcomes: ['Tell time to the hour', 'Tell time to half hour', 'Read analog clocks'],
          activities: ['Clock reading', 'Time practice', 'Daily schedule'],
          resources: ['Teaching clocks', 'Time worksheets', 'Schedule cards']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate addition/subtraction skills', 'Show measurement understanding', 'Apply problem solving'],
          activities: ['Assessment tasks', 'Review activities', 'Progress review'],
          resources: ['Assessment materials', 'Review games', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Two-Digit Operations & Data',
      scheme: [
        {
          week: 1,
          topic: 'Adding Two-Digit Numbers (No Regrouping)',
          strand: 'Number & Operations',
          learningOutcomes: ['Add two-digit numbers without regrouping', 'Use place value for addition', 'Check answers'],
          activities: ['Place value addition', 'Base ten practice', 'Checking work'],
          resources: ['Base ten blocks', 'Place value mats', 'Worksheets']
        },
        {
          week: 2,
          topic: 'Adding Two-Digit Numbers (With Regrouping)',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand regrouping', 'Add with regrouping', 'Represent addition'],
          activities: ['Regrouping introduction', 'Practice problems', 'Visual representations'],
          resources: ['Base ten blocks', 'Regrouping mats', 'Practice sheets']
        },
        {
          week: 3,
          topic: 'Subtracting Two-Digit Numbers (No Regrouping)',
          strand: 'Number & Operations',
          learningOutcomes: ['Subtract two-digit numbers', 'Use place value for subtraction', 'Solve problems'],
          activities: ['Place value subtraction', 'Practice problems', 'Word problems'],
          resources: ['Base ten blocks', 'Place value mats', 'Problem cards']
        },
        {
          week: 4,
          topic: 'Collecting and Organizing Data',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Collect data using surveys', 'Organize data in tables', 'Answer questions from data'],
          activities: ['Survey activities', 'Table creation', 'Data analysis'],
          resources: ['Survey templates', 'Tally sheets', 'Data tables']
        },
        {
          week: 5,
          topic: 'Subtracting Two-Digit Numbers (With Regrouping)',
          strand: 'Number & Operations',
          learningOutcomes: ['Subtract with regrouping', 'Check subtraction with addition', 'Solve word problems'],
          activities: ['Regrouping subtraction', 'Checking strategies', 'Problem solving'],
          resources: ['Base ten blocks', 'Practice sheets', 'Word problems']
        },
        {
          week: 6,
          topic: 'Picture Graphs and Bar Graphs',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Create picture graphs', 'Create simple bar graphs', 'Interpret graphs'],
          activities: ['Graph creation', 'Graph interpretation', 'Comparison activities'],
          resources: ['Graph paper', 'Data sets', 'Interpretation worksheets']
        },
        {
          week: 7,
          topic: 'Money: Counting Coins',
          strand: 'Number & Operations',
          learningOutcomes: ['Count mixed coins', 'Find total value', 'Make amounts with coins'],
          activities: ['Coin counting', 'Value calculation', 'Making amounts'],
          resources: ['Play coins', 'Counting mats', 'Problem cards']
        },
        {
          week: 8,
          topic: '3D Shapes Properties',
          strand: 'Geometry',
          learningOutcomes: ['Identify 3D shape properties', 'Count faces, edges, vertices', 'Relate 3D to 2D shapes'],
          activities: ['Property exploration', 'Counting activities', 'Shape relationships'],
          resources: ['3D models', 'Property charts', 'Worksheets']
        },
        {
          week: 9,
          topic: 'Mixed Operations Practice',
          strand: 'Number & Operations',
          learningOutcomes: ['Choose correct operation', 'Solve mixed problems', 'Explain reasoning'],
          activities: ['Operation selection', 'Mixed practice', 'Explanation activities'],
          resources: ['Problem cards', 'Explanation frames', 'Practice sheets']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Show two-digit operation skills', 'Demonstrate data skills', 'Apply money concepts'],
          activities: ['Assessment tasks', 'Skills demonstration', 'Portfolio review'],
          resources: ['Assessment materials', 'Portfolios', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Multiplication Introduction & Measurement',
      scheme: [
        {
          week: 1,
          topic: 'Skip Counting by 2s, 5s, 10s',
          strand: 'Number & Operations',
          learningOutcomes: ['Skip count fluently', 'Identify patterns', 'Use skip counting for problem solving'],
          activities: ['Skip counting songs', 'Pattern recognition', 'Application problems'],
          resources: ['Skip counting charts', 'Number lines', 'Pattern materials']
        },
        {
          week: 2,
          topic: 'Arrays and Equal Groups',
          strand: 'Number & Operations',
          learningOutcomes: ['Arrange objects in arrays', 'Identify equal groups', 'Count total in arrays'],
          activities: ['Array building', 'Equal group activities', 'Array counting'],
          resources: ['Counters', 'Array cards', 'Grid paper']
        },
        {
          week: 3,
          topic: 'Introduction to Multiplication',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand multiplication as repeated addition', 'Use arrays for multiplication', 'Write multiplication sentences'],
          activities: ['Concept introduction', 'Array multiplication', 'Writing equations'],
          resources: ['Arrays', 'Manipulatives', 'Equation cards']
        },
        {
          week: 4,
          topic: 'Measuring in Centimeters and Meters',
          strand: 'Measurement',
          learningOutcomes: ['Measure in cm and m', 'Choose appropriate unit', 'Estimate and measure'],
          activities: ['Measurement practice', 'Unit selection', 'Estimation activities'],
          resources: ['Rulers', 'Meter sticks', 'Measuring tasks']
        },
        {
          week: 5,
          topic: 'Multiplication Facts: 2s and 5s',
          strand: 'Number & Operations',
          learningOutcomes: ['Learn 2 times table', 'Learn 5 times table', 'Apply facts to problems'],
          activities: ['Times table practice', 'Fact games', 'Application problems'],
          resources: ['Times table charts', 'Flash cards', 'Problem cards']
        },
        {
          week: 6,
          topic: 'Introduction to Division',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand division as sharing equally', 'Divide objects into groups', 'Relate division to multiplication'],
          activities: ['Sharing activities', 'Grouping practice', 'Related facts'],
          resources: ['Counters', 'Sharing mats', 'Division cards']
        },
        {
          week: 7,
          topic: 'Telling Time to Quarter Hour',
          strand: 'Measurement',
          learningOutcomes: ['Tell time to quarter hour', 'Use quarter past and quarter to', 'Read analog and digital'],
          activities: ['Clock practice', 'Time vocabulary', 'Mixed reading'],
          resources: ['Clocks', 'Time cards', 'Worksheets']
        },
        {
          week: 8,
          topic: 'Fractions: Halves and Quarters',
          strand: 'Number & Operations',
          learningOutcomes: ['Identify halves', 'Identify quarters', 'Partition shapes into fractions'],
          activities: ['Fraction exploration', 'Shape partitioning', 'Fraction naming'],
          resources: ['Fraction circles', 'Shape templates', 'Fraction cards']
        },
        {
          week: 9,
          topic: 'Multiplication Facts: 10s',
          strand: 'Number & Operations',
          learningOutcomes: ['Learn 10 times table', 'Identify patterns in 10s', 'Apply to problem solving'],
          activities: ['Times table practice', 'Pattern discovery', 'Application'],
          resources: ['Times table chart', 'Pattern materials', 'Problems']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate multiplication understanding', 'Show measurement skills', 'Apply fraction concepts'],
          activities: ['Assessment tasks', 'Skills review', 'Progress check'],
          resources: ['Assessment materials', 'Review activities', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Consolidation & Problem Solving',
      scheme: [
        {
          week: 1,
          topic: 'Numbers to 1000',
          strand: 'Number & Operations',
          learningOutcomes: ['Read and write numbers to 1000', 'Understand hundreds, tens, ones', 'Compare three-digit numbers'],
          activities: ['Place value extension', 'Number reading/writing', 'Comparison activities'],
          resources: ['Place value materials', 'Number cards', 'Comparison worksheets']
        },
        {
          week: 2,
          topic: 'Addition and Subtraction to 100',
          strand: 'Number & Operations',
          learningOutcomes: ['Add/subtract with regrouping fluently', 'Check answers', 'Solve multi-step problems'],
          activities: ['Fluency practice', 'Checking strategies', 'Multi-step problems'],
          resources: ['Practice materials', 'Problem cards', 'Checking worksheets']
        },
        {
          week: 3,
          topic: 'Weight and Mass',
          strand: 'Measurement',
          learningOutcomes: ['Measure mass in grams and kilograms', 'Choose appropriate units', 'Compare masses'],
          activities: ['Weighing activities', 'Unit selection', 'Mass comparison'],
          resources: ['Scales', 'Objects', 'Recording sheets']
        },
        {
          week: 4,
          topic: 'Multiplication and Division Review',
          strand: 'Number & Operations',
          learningOutcomes: ['Review all multiplication facts', 'Apply division concepts', 'Solve word problems'],
          activities: ['Fact review', 'Division practice', 'Word problems'],
          resources: ['Flash cards', 'Problem cards', 'Practice sheets']
        },
        {
          week: 5,
          topic: 'Capacity and Volume',
          strand: 'Measurement',
          learningOutcomes: ['Measure capacity in liters', 'Compare volumes', 'Estimate capacity'],
          activities: ['Measuring activities', 'Volume comparison', 'Estimation practice'],
          resources: ['Containers', 'Measuring cups', 'Water/sand']
        },
        {
          week: 6,
          topic: 'Fractions: Thirds',
          strand: 'Number & Operations',
          learningOutcomes: ['Identify thirds', 'Compare fractions', 'Represent fractions'],
          activities: ['Fraction exploration', 'Comparison activities', 'Fraction representation'],
          resources: ['Fraction materials', 'Comparison cards', 'Drawing materials']
        },
        {
          week: 7,
          topic: 'Geometry: Lines and Angles',
          strand: 'Geometry',
          learningOutcomes: ['Identify straight and curved lines', 'Recognize right angles', 'Find angles in environment'],
          activities: ['Line exploration', 'Angle hunting', 'Environmental connections'],
          resources: ['Line examples', 'Angle finders', 'Pictures']
        },
        {
          week: 8,
          topic: 'Problem Solving Strategies',
          strand: 'All Strands',
          learningOutcomes: ['Use draw a picture strategy', 'Use make a list strategy', 'Choose appropriate strategy'],
          activities: ['Strategy practice', 'Strategy selection', 'Problem solving'],
          resources: ['Problem cards', 'Strategy posters', 'Recording materials']
        },
        {
          week: 9,
          topic: 'Year 2 Review',
          strand: 'All Strands',
          learningOutcomes: ['Review all Year 2 concepts', 'Identify strengths', 'Prepare for Year 3'],
          activities: ['Comprehensive review', 'Skill assessment', 'Goal setting'],
          resources: ['Review materials', 'Assessment tools', 'Goal worksheets']
        },
        {
          week: 10,
          topic: 'Year 2 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Complete final assessment', 'Celebrate achievements', 'Transition to Year 3'],
          activities: ['Final testing', 'Awards ceremony', 'Celebration'],
          resources: ['Assessment materials', 'Certificates', 'Celebration supplies']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 3 MATHEMATICS CURRICULUM
// Multiplication & Division Focus
// ============================================
export const mathYear3 = {
  year: 3,
  subject: 'Mathematics',
  totalWeeks: 40,
  periodsPerWeek: 6,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Place Value to 1000 & Multiplication',
      scheme: [
        {
          week: 1,
          topic: 'Place Value to 1000',
          strand: 'Number & Operations',
          learningOutcomes: ['Read/write numbers to 1000', 'Expand numbers by place value', 'Compare and order numbers'],
          activities: ['Place value exploration', 'Expanded form', 'Ordering activities'],
          resources: ['Base ten blocks', 'Place value charts', 'Number cards']
        },
        {
          week: 2,
          topic: 'Addition and Subtraction Review',
          strand: 'Number & Operations',
          learningOutcomes: ['Add/subtract within 1000', 'Use efficient strategies', 'Estimate answers'],
          activities: ['Strategy review', 'Estimation practice', 'Problem solving'],
          resources: ['Practice materials', 'Estimation activities', 'Word problems']
        },
        {
          week: 3,
          topic: 'Multiplication Facts: 3s and 4s',
          strand: 'Number & Operations',
          learningOutcomes: ['Learn 3 times table', 'Learn 4 times table', 'Relate to skip counting'],
          activities: ['Times table practice', 'Skip counting connection', 'Fact games'],
          resources: ['Times tables', 'Flash cards', 'Games']
        },
        {
          week: 4,
          topic: 'Arrays and Area',
          strand: 'Number & Operations',
          learningOutcomes: ['Use arrays for multiplication', 'Understand area as covering', 'Calculate simple areas'],
          activities: ['Array building', 'Area covering', 'Area calculation'],
          resources: ['Grid paper', 'Square tiles', 'Area worksheets']
        },
        {
          week: 5,
          topic: 'Multiplication Facts: 6s and 7s',
          strand: 'Number & Operations',
          learningOutcomes: ['Learn 6 times table', 'Learn 7 times table', 'Apply facts fluently'],
          activities: ['Times table instruction', 'Practice games', 'Application'],
          resources: ['Times table charts', 'Games', 'Practice sheets']
        },
        {
          week: 6,
          topic: 'Perimeter',
          strand: 'Measurement',
          learningOutcomes: ['Understand perimeter concept', 'Measure perimeters', 'Calculate perimeters'],
          activities: ['Perimeter exploration', 'Measuring activities', 'Calculation practice'],
          resources: ['Shape cards', 'Rulers', 'Perimeter worksheets']
        },
        {
          week: 7,
          topic: 'Multiplication Facts: 8s and 9s',
          strand: 'Number & Operations',
          learningOutcomes: ['Learn 8 times table', 'Learn 9 times table', 'Use patterns and tricks'],
          activities: ['Times table practice', 'Pattern discovery', '9s finger trick'],
          resources: ['Times tables', 'Pattern materials', 'Practice sheets']
        },
        {
          week: 8,
          topic: 'Properties of Multiplication',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand commutative property', 'Understand associative property', 'Apply properties'],
          activities: ['Property exploration', 'Application practice', 'Problem solving'],
          resources: ['Property examples', 'Manipulatives', 'Practice problems']
        },
        {
          week: 9,
          topic: 'Division Facts',
          strand: 'Number & Operations',
          learningOutcomes: ['Relate division to multiplication', 'Learn division facts', 'Solve division problems'],
          activities: ['Fact families', 'Division practice', 'Problem solving'],
          resources: ['Fact family cards', 'Division materials', 'Problem cards']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate multiplication knowledge', 'Show division understanding', 'Apply measurement skills'],
          activities: ['Assessment tasks', 'Skills review', 'Progress evaluation'],
          resources: ['Assessment materials', 'Review activities', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Fractions & Measurement',
      scheme: [
        {
          week: 1,
          topic: 'Fractions: Equal Parts',
          strand: 'Number & Operations',
          learningOutcomes: ['Identify equal and unequal parts', 'Partition shapes into equal parts', 'Name fractions'],
          activities: ['Partitioning activities', 'Equal parts identification', 'Fraction naming'],
          resources: ['Fraction materials', 'Shape templates', 'Partitioning tools']
        },
        {
          week: 2,
          topic: 'Fractions: Halves, Thirds, Quarters',
          strand: 'Number & Operations',
          learningOutcomes: ['Represent unit fractions', 'Compare unit fractions', 'Order fractions'],
          activities: ['Fraction representation', 'Comparison activities', 'Ordering practice'],
          resources: ['Fraction circles', 'Fraction bars', 'Comparison cards']
        },
        {
          week: 3,
          topic: 'Fractions on a Number Line',
          strand: 'Number & Operations',
          learningOutcomes: ['Place fractions on number line', 'Identify fraction locations', 'Compare using number line'],
          activities: ['Number line work', 'Fraction placement', 'Comparison practice'],
          resources: ['Number lines', 'Fraction markers', 'Worksheets']
        },
        {
          week: 4,
          topic: 'Time: Minutes',
          strand: 'Measurement',
          learningOutcomes: ['Tell time to 5 minutes', 'Calculate elapsed time', 'Solve time problems'],
          activities: ['Clock reading', 'Elapsed time', 'Problem solving'],
          resources: ['Clocks', 'Time worksheets', 'Problem cards']
        },
        {
          week: 5,
          topic: 'Equivalent Fractions',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand equivalent fractions', 'Find equivalent fractions', 'Use models to show equivalence'],
          activities: ['Equivalence exploration', 'Finding equivalents', 'Model creation'],
          resources: ['Fraction materials', 'Equivalence charts', 'Practice sheets']
        },
        {
          week: 6,
          topic: 'Length: Meters, Centimeters, Millimeters',
          strand: 'Measurement',
          learningOutcomes: ['Convert between units', 'Measure accurately', 'Solve measurement problems'],
          activities: ['Unit conversion', 'Measurement practice', 'Problem solving'],
          resources: ['Measuring tools', 'Conversion charts', 'Problem cards']
        },
        {
          week: 7,
          topic: 'Comparing and Ordering Fractions',
          strand: 'Number & Operations',
          learningOutcomes: ['Compare fractions with same denominator', 'Compare fractions with same numerator', 'Order fractions'],
          activities: ['Fraction comparison', 'Ordering activities', 'Reasoning practice'],
          resources: ['Fraction materials', 'Comparison cards', 'Ordering activities']
        },
        {
          week: 8,
          topic: 'Mass: Grams and Kilograms',
          strand: 'Measurement',
          learningOutcomes: ['Measure mass accurately', 'Convert between g and kg', 'Solve mass problems'],
          activities: ['Weighing practice', 'Unit conversion', 'Problem solving'],
          resources: ['Scales', 'Weights', 'Problem cards']
        },
        {
          week: 9,
          topic: 'Adding Fractions (Same Denominator)',
          strand: 'Number & Operations',
          learningOutcomes: ['Add fractions with like denominators', 'Model fraction addition', 'Solve problems'],
          activities: ['Addition modeling', 'Calculation practice', 'Problem solving'],
          resources: ['Fraction models', 'Worksheets', 'Problem cards']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate fraction understanding', 'Show measurement skills', 'Apply concepts'],
          activities: ['Assessment tasks', 'Skills demonstration', 'Portfolio review'],
          resources: ['Assessment materials', 'Portfolios', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Geometry & Data',
      scheme: [
        {
          week: 1,
          topic: 'Lines: Parallel and Perpendicular',
          strand: 'Geometry',
          learningOutcomes: ['Identify parallel lines', 'Identify perpendicular lines', 'Find in environment'],
          activities: ['Line identification', 'Drawing practice', 'Environmental search'],
          resources: ['Line examples', 'Rulers', 'Pictures']
        },
        {
          week: 2,
          topic: 'Angles: Right, Acute, Obtuse',
          strand: 'Geometry',
          learningOutcomes: ['Classify angles', 'Measure angles approximately', 'Find angles in shapes'],
          activities: ['Angle classification', 'Measurement introduction', 'Shape analysis'],
          resources: ['Angle cards', 'Protractors', 'Shape materials']
        },
        {
          week: 3,
          topic: 'Quadrilaterals',
          strand: 'Geometry',
          learningOutcomes: ['Identify types of quadrilaterals', 'Classify by properties', 'Draw quadrilaterals'],
          activities: ['Shape identification', 'Property classification', 'Drawing practice'],
          resources: ['Shape cards', 'Property charts', 'Drawing materials']
        },
        {
          week: 4,
          topic: 'Data Collection and Tables',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Design data collection', 'Organize in tables', 'Analyze data'],
          activities: ['Survey design', 'Table creation', 'Data analysis'],
          resources: ['Survey templates', 'Table formats', 'Analysis guides']
        },
        {
          week: 5,
          topic: 'Triangles',
          strand: 'Geometry',
          learningOutcomes: ['Classify triangles by sides', 'Classify triangles by angles', 'Identify triangle properties'],
          activities: ['Triangle classification', 'Property exploration', 'Drawing triangles'],
          resources: ['Triangle cards', 'Classification charts', 'Drawing tools']
        },
        {
          week: 6,
          topic: 'Bar Graphs',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Create scaled bar graphs', 'Interpret bar graphs', 'Compare data'],
          activities: ['Graph creation', 'Interpretation practice', 'Data comparison'],
          resources: ['Graph paper', 'Data sets', 'Interpretation questions']
        },
        {
          week: 7,
          topic: 'Area of Rectangles',
          strand: 'Measurement',
          learningOutcomes: ['Calculate area using formula', 'Apply to rectangles and squares', 'Solve area problems'],
          activities: ['Formula introduction', 'Calculation practice', 'Problem solving'],
          resources: ['Grid paper', 'Area worksheets', 'Problem cards']
        },
        {
          week: 8,
          topic: 'Pictographs',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Create pictographs with scale', 'Interpret pictographs', 'Answer questions'],
          activities: ['Graph creation', 'Interpretation', 'Question answering'],
          resources: ['Graph materials', 'Symbol cards', 'Data sets']
        },
        {
          week: 9,
          topic: 'Symmetry and Transformations',
          strand: 'Geometry',
          learningOutcomes: ['Identify multiple lines of symmetry', 'Perform simple translations', 'Recognize rotational symmetry'],
          activities: ['Symmetry exploration', 'Translation practice', 'Rotation introduction'],
          resources: ['Mirrors', 'Transformation materials', 'Grid paper']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate geometry knowledge', 'Show data skills', 'Apply measurement'],
          activities: ['Assessment tasks', 'Skills review', 'Progress check'],
          resources: ['Assessment materials', 'Review activities', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Multi-Digit Operations & Problem Solving',
      scheme: [
        {
          week: 1,
          topic: 'Multi-Digit Addition',
          strand: 'Number & Operations',
          learningOutcomes: ['Add multi-digit numbers', 'Use standard algorithm', 'Check with estimation'],
          activities: ['Algorithm practice', 'Multi-digit addition', 'Estimation checking'],
          resources: ['Place value materials', 'Worksheets', 'Problem cards']
        },
        {
          week: 2,
          topic: 'Multi-Digit Subtraction',
          strand: 'Number & Operations',
          learningOutcomes: ['Subtract multi-digit numbers', 'Use standard algorithm', 'Solve word problems'],
          activities: ['Algorithm practice', 'Problem solving', 'Checking work'],
          resources: ['Place value materials', 'Worksheets', 'Word problems']
        },
        {
          week: 3,
          topic: 'Multiplication by Multiples of 10',
          strand: 'Number & Operations',
          learningOutcomes: ['Multiply by 10, 100', 'Identify patterns', 'Apply to problem solving'],
          activities: ['Pattern discovery', 'Multiplication practice', 'Application'],
          resources: ['Pattern materials', 'Practice sheets', 'Problem cards']
        },
        {
          week: 4,
          topic: 'Capacity: Liters and Milliliters',
          strand: 'Measurement',
          learningOutcomes: ['Measure capacity accurately', 'Convert between L and mL', 'Solve capacity problems'],
          activities: ['Measuring practice', 'Conversion work', 'Problem solving'],
          resources: ['Measuring containers', 'Conversion charts', 'Problems']
        },
        {
          week: 5,
          topic: 'Two-Digit by One-Digit Multiplication',
          strand: 'Number & Operations',
          learningOutcomes: ['Multiply using area model', 'Use partial products', 'Apply to problems'],
          activities: ['Area model practice', 'Partial products', 'Problem solving'],
          resources: ['Grid paper', 'Multiplication materials', 'Problems']
        },
        {
          week: 6,
          topic: 'Division with Remainders',
          strand: 'Number & Operations',
          learningOutcomes: ['Divide with remainders', 'Interpret remainders', 'Solve division problems'],
          activities: ['Division practice', 'Remainder interpretation', 'Problem solving'],
          resources: ['Counters', 'Division materials', 'Problem cards']
        },
        {
          week: 7,
          topic: 'Money: Making Change',
          strand: 'Number & Operations',
          learningOutcomes: ['Calculate change', 'Count up to make change', 'Solve money problems'],
          activities: ['Change practice', 'Counting up', 'Problem solving'],
          resources: ['Play money', 'Price tags', 'Problem scenarios']
        },
        {
          week: 8,
          topic: 'Multi-Step Problems',
          strand: 'All Strands',
          learningOutcomes: ['Identify steps in problems', 'Solve multi-step problems', 'Explain solutions'],
          activities: ['Problem analysis', 'Multi-step solving', 'Solution explanation'],
          resources: ['Multi-step problems', 'Recording sheets', 'Discussion guides']
        },
        {
          week: 9,
          topic: 'Year 3 Review',
          strand: 'All Strands',
          learningOutcomes: ['Review all Year 3 concepts', 'Identify mastered skills', 'Prepare for Year 4'],
          activities: ['Comprehensive review', 'Skills assessment', 'Goal setting'],
          resources: ['Review materials', 'Assessment tools', 'Goal worksheets']
        },
        {
          week: 10,
          topic: 'Year 3 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Complete final assessment', 'Celebrate achievements', 'Transition to Year 4'],
          activities: ['Final testing', 'Awards ceremony', 'Celebration'],
          resources: ['Assessment materials', 'Certificates', 'Celebration supplies']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 4 MATHEMATICS CURRICULUM
// Building Computational Fluency
// ============================================
export const mathYear4 = {
  year: 4,
  subject: 'Mathematics',
  totalWeeks: 40,
  periodsPerWeek: 6,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Number, Operations (Addition & Subtraction) and Patterns',
      scheme: [
        {
          week: 1,
          topic: 'Whole Numbers to 100,000 - Place Value',
          strand: 'Number & Operations',
          learningOutcomes: ['Read and write numbers to 100,000', 'Understand place value up to hundred thousands', 'Represent numbers in expanded form'],
          activities: ['Place value exploration to 100,000', 'Number reading and writing', 'Expanded form practice'],
          resources: ['Place value charts', 'Number cards to 100,000', 'Worksheets']
        },
        {
          week: 2,
          topic: 'Comparing and Ordering Whole Numbers',
          strand: 'Number & Operations',
          learningOutcomes: ['Compare numbers up to 100,000 using < > =', 'Order numbers from smallest to largest and vice versa', 'Use number lines to compare and order'],
          activities: ['Number comparison activities', 'Ordering exercises', 'Number line placement'],
          resources: ['Number lines', 'Comparison cards', 'Ordering worksheets']
        },
        {
          week: 3,
          topic: 'Addition of Whole Numbers up to 5 Digits',
          strand: 'Number & Operations',
          learningOutcomes: ['Add numbers up to 5 digits with regrouping', 'Solve addition word problems', 'Estimate sums to check answers'],
          activities: ['Multi-digit addition practice', 'Word problem solving', 'Estimation and checking'],
          resources: ['Addition worksheets', 'Word problems', 'Place value materials']
        },
        {
          week: 4,
          topic: 'Subtraction of Whole Numbers up to 5 Digits',
          strand: 'Number & Operations',
          learningOutcomes: ['Subtract numbers up to 5 digits with regrouping', 'Solve subtraction word problems', 'Use estimation to verify answers'],
          activities: ['Multi-digit subtraction practice', 'Word problem solving', 'Checking with addition'],
          resources: ['Subtraction worksheets', 'Word problems', 'Practice sheets']
        },
        {
          week: 5,
          topic: 'Addition and Subtraction - Multi-Step Problems',
          strand: 'Number & Operations',
          learningOutcomes: ['Solve multi-step addition and subtraction problems', 'Choose correct operation for word problems', 'Apply mental strategies for computation'],
          activities: ['Multi-step problems', 'Operation selection practice', 'Mental math strategies'],
          resources: ['Problem sets', 'Strategy cards', 'Practice materials']
        },
        {
          week: 6,
          topic: 'Addition and Subtraction - Real Life Applications',
          strand: 'Number & Operations',
          learningOutcomes: ['Apply addition and subtraction to everyday situations', 'Solve problems involving money and measurement contexts', 'Explain solution strategies'],
          activities: ['Real-life problem solving', 'Money-based problems', 'Group problem discussions'],
          resources: ['Real-life scenario cards', 'Play money', 'Measurement contexts']
        },
        {
          week: 7,
          topic: 'Number Patterns - Identifying and Extending',
          strand: 'Patterns',
          learningOutcomes: ['Identify number patterns involving addition and subtraction', 'Extend number patterns', 'Describe pattern rules in words'],
          activities: ['Pattern identification', 'Pattern extension', 'Rule description'],
          resources: ['Pattern cards', 'Number sequences', 'Worksheets']
        },
        {
          week: 8,
          topic: 'Number Patterns - Creating Rules and Applying',
          strand: 'Patterns',
          learningOutcomes: ['Create own number patterns', 'Write rules for patterns', 'Apply patterns to solve problems'],
          activities: ['Pattern creation', 'Rule writing', 'Pattern problem solving'],
          resources: ['Pattern materials', 'Rule charts', 'Problem sets']
        },
        {
          week: 9,
          topic: 'Revision - Number, Operations and Patterns',
          strand: 'Number & Operations',
          learningOutcomes: ['Consolidate addition and subtraction skills', 'Review pattern concepts', 'Apply skills to mixed problems'],
          activities: ['Skills revision', 'Mixed practice', 'Error correction exercises'],
          resources: ['Revision worksheets', 'Mixed problem sets', 'Self-assessment sheets']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate number and operations skills', 'Show pattern knowledge', 'Apply problem solving strategies'],
          activities: ['Written assessment', 'Practical tasks', 'Progress evaluation'],
          resources: ['Assessment papers', 'Recording sheets', 'Marking guides']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Number, Operations (Multiplication & Division) and Patterns; Length, Lines, Angles and Area',
      scheme: [
        {
          week: 1,
          topic: 'Multiplication of Whole Numbers',
          strand: 'Number & Operations',
          learningOutcomes: ['Multiply up to 4-digit by 1-digit numbers', 'Use the standard multiplication algorithm', 'Solve multiplication word problems'],
          activities: ['Multiplication algorithm practice', 'Word problem solving', 'Mental multiplication strategies'],
          resources: ['Multiplication worksheets', 'Word problems', 'Practice sheets']
        },
        {
          week: 2,
          topic: 'Multiplication - Two-Digit by Two-Digit',
          strand: 'Number & Operations',
          learningOutcomes: ['Multiply two-digit by two-digit numbers', 'Use area model for multiplication', 'Estimate products to check answers'],
          activities: ['Two-digit multiplication practice', 'Area model work', 'Estimation activities'],
          resources: ['Grid paper', 'Multiplication worksheets', 'Estimation practice']
        },
        {
          week: 3,
          topic: 'Division of Whole Numbers',
          strand: 'Number & Operations',
          learningOutcomes: ['Divide up to 3-digit by 1-digit numbers', 'Interpret remainders in context', 'Check division using multiplication'],
          activities: ['Division practice', 'Remainder interpretation', 'Checking with multiplication'],
          resources: ['Division worksheets', 'Word problems', 'Practice materials']
        },
        {
          week: 4,
          topic: 'Multiplication and Division Problem Solving',
          strand: 'Number & Operations',
          learningOutcomes: ['Solve word problems involving multiplication and division', 'Choose correct operation', 'Apply inverse relationship between multiplication and division'],
          activities: ['Mixed operation problems', 'Operation selection', 'Inverse relationship practice'],
          resources: ['Problem sets', 'Strategy cards', 'Practice worksheets']
        },
        {
          week: 5,
          topic: 'Patterns with Multiplication and Division',
          strand: 'Patterns',
          learningOutcomes: ['Identify patterns in multiplication and division', 'Extend and create patterns using all four operations', 'Solve pattern problems'],
          activities: ['Multiplication/division pattern exploration', 'Pattern creation', 'Problem solving'],
          resources: ['Multiplication charts', 'Pattern worksheets', 'Practice materials']
        },
        {
          week: 6,
          topic: 'Length - Estimation, Measurement and Comparison',
          strand: 'Measurement',
          learningOutcomes: ['Estimate and measure lengths using standard units (mm, cm, m, km)', 'Compare and order lengths', 'Solve problems involving length and perimeter'],
          activities: ['Length estimation', 'Measuring activities', 'Perimeter calculations'],
          resources: ['Rulers', 'Measuring tapes', 'Length worksheets']
        },
        {
          week: 7,
          topic: 'Lines and Angles',
          strand: 'Geometry',
          learningOutcomes: ['Identify and draw types of lines (parallel, perpendicular)', 'Classify angles (right, acute, obtuse)', 'Measure angles using a protractor'],
          activities: ['Line identification and drawing', 'Angle classification', 'Protractor measurement'],
          resources: ['Protractors', 'Rulers', 'Angle worksheets']
        },
        {
          week: 8,
          topic: 'Area - Estimation and Calculation',
          strand: 'Measurement',
          learningOutcomes: ['Estimate and calculate area of rectangles and squares', 'Apply area formula (L x W)', 'Solve area word problems'],
          activities: ['Area estimation', 'Formula application', 'Problem solving'],
          resources: ['Grid paper', 'Area worksheets', 'Measurement tools']
        },
        {
          week: 9,
          topic: 'Revision - Operations, Measurement and Geometry',
          strand: 'All Strands',
          learningOutcomes: ['Consolidate multiplication and division skills', 'Review length, angles and area', 'Apply skills to mixed problems'],
          activities: ['Skills revision', 'Mixed practice', 'Error correction exercises'],
          resources: ['Revision worksheets', 'Mixed problem sets', 'Self-assessment sheets']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate multiplication and division skills', 'Show understanding of length, lines, angles and area', 'Apply problem solving strategies'],
          activities: ['Written assessment', 'Practical tasks', 'Progress evaluation'],
          resources: ['Assessment papers', 'Recording sheets', 'Marking guides']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Fractions, Decimals; Plane Shapes, Solid Shapes, Capacity and Volume, Grids and Coordinates',
      scheme: [
        {
          week: 1,
          topic: 'Fractions - Representing and Comparing',
          strand: 'Number & Operations',
          learningOutcomes: ['Represent fractions using models and number lines', 'Find equivalent fractions', 'Compare and order fractions'],
          activities: ['Fraction representation', 'Equivalence exploration', 'Comparison activities'],
          resources: ['Fraction strips', 'Number lines', 'Comparison worksheets']
        },
        {
          week: 2,
          topic: 'Fractions - Addition and Subtraction',
          strand: 'Number & Operations',
          learningOutcomes: ['Add fractions with like denominators', 'Subtract fractions with like denominators', 'Solve fraction word problems'],
          activities: ['Fraction addition practice', 'Fraction subtraction practice', 'Word problems'],
          resources: ['Fraction models', 'Worksheets', 'Word problem cards']
        },
        {
          week: 3,
          topic: 'Decimal Numbers',
          strand: 'Number & Operations',
          learningOutcomes: ['Read and write decimal numbers to hundredths', 'Relate decimals to fractions', 'Compare and order decimals'],
          activities: ['Decimal reading/writing', 'Decimal-fraction connection', 'Ordering activities'],
          resources: ['Decimal charts', 'Place value materials', 'Comparison worksheets']
        },
        {
          week: 4,
          topic: 'Fractions and Decimals - Mixed Practice',
          strand: 'Number & Operations',
          learningOutcomes: ['Convert between simple fractions and decimals', 'Solve problems involving fractions and decimals', 'Apply to real-life money contexts'],
          activities: ['Conversion practice', 'Mixed problem solving', 'Money applications'],
          resources: ['Conversion charts', 'Practice worksheets', 'Play money']
        },
        {
          week: 5,
          topic: 'Plane Shapes - Properties and Classification',
          strand: 'Geometry',
          learningOutcomes: ['Identify and classify plane shapes by properties', 'Describe properties of triangles and quadrilaterals', 'Draw plane shapes accurately'],
          activities: ['Shape classification', 'Property identification', 'Shape drawing'],
          resources: ['Shape cards', 'Classification charts', 'Drawing materials']
        },
        {
          week: 6,
          topic: 'Solid Shapes - Properties and Nets',
          strand: 'Geometry',
          learningOutcomes: ['Identify and describe properties of solid shapes', 'Identify faces, edges and vertices', 'Construct nets of simple solids'],
          activities: ['Solid shape exploration', 'Property recording', 'Net construction'],
          resources: ['3D shape models', 'Net templates', 'Construction materials']
        },
        {
          week: 7,
          topic: 'Capacity and Volume',
          strand: 'Measurement',
          learningOutcomes: ['Estimate and measure capacity using standard units (mL, L)', 'Compare and order capacities', 'Solve problems involving capacity and volume'],
          activities: ['Capacity measurement', 'Comparison activities', 'Problem solving'],
          resources: ['Measuring containers', 'Capacity worksheets', 'Water/sand for measurement']
        },
        {
          week: 8,
          topic: 'Grids and Coordinates',
          strand: 'Geometry',
          learningOutcomes: ['Read and plot coordinates on a grid', 'Describe positions using coordinates', 'Follow and give directions using grids'],
          activities: ['Coordinate plotting', 'Position description', 'Direction activities'],
          resources: ['Coordinate grids', 'Plotting worksheets', 'Map activities']
        },
        {
          week: 9,
          topic: 'Revision - Fractions, Decimals, Shapes and Measurement',
          strand: 'All Strands',
          learningOutcomes: ['Consolidate fraction and decimal skills', 'Review shape properties and capacity', 'Apply coordinate knowledge'],
          activities: ['Skills revision', 'Mixed practice', 'Error correction exercises'],
          resources: ['Revision worksheets', 'Mixed problem sets', 'Self-assessment sheets']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate fraction and decimal skills', 'Show understanding of shapes and capacity', 'Apply coordinate and geometry knowledge'],
          activities: ['Written assessment', 'Practical tasks', 'Progress evaluation'],
          resources: ['Assessment papers', 'Recording sheets', 'Marking guides']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Mapping, Time, Money, Mass; Chance and Data; End-of-Year Revision and Assessment',
      scheme: [
        {
          week: 1,
          topic: 'Mapping and Location',
          strand: 'Geometry',
          learningOutcomes: ['Read and interpret simple maps', 'Use compass directions (N, S, E, W)', 'Describe routes and locations on maps'],
          activities: ['Map reading', 'Compass direction practice', 'Route description'],
          resources: ['Simple maps', 'Compasses', 'Map worksheets']
        },
        {
          week: 2,
          topic: 'Time - Reading, Calculating and Elapsed Time',
          strand: 'Measurement',
          learningOutcomes: ['Read time on analog and digital clocks', 'Calculate elapsed time', 'Solve time word problems including timetables'],
          activities: ['Clock reading', 'Elapsed time practice', 'Timetable problems'],
          resources: ['Analog clocks', 'Digital clocks', 'Time worksheets']
        },
        {
          week: 3,
          topic: 'Money - Solving Problems with Vatu',
          strand: 'Measurement',
          learningOutcomes: ['Solve problems involving money (Vatu)', 'Calculate totals, change and budgets', 'Apply all four operations to money problems'],
          activities: ['Money problem solving', 'Shopping scenarios', 'Budget activities'],
          resources: ['Play money (Vatu)', 'Price lists', 'Budget worksheets']
        },
        {
          week: 4,
          topic: 'Mass - Estimation, Measurement and Comparison',
          strand: 'Measurement',
          learningOutcomes: ['Estimate and measure mass using standard units (g, kg)', 'Compare and order masses', 'Solve problems involving mass'],
          activities: ['Mass estimation', 'Weighing activities', 'Comparison and problem solving'],
          resources: ['Scales/balances', 'Objects to weigh', 'Mass worksheets']
        },
        {
          week: 5,
          topic: 'Chance and Probability',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Use chance vocabulary (certain, likely, unlikely, impossible)', 'Describe likelihood of events', 'Conduct simple probability experiments'],
          activities: ['Probability vocabulary', 'Likelihood discussion', 'Probability experiments'],
          resources: ['Dice', 'Spinners', 'Probability worksheets']
        },
        {
          week: 6,
          topic: 'Data Collection, Organisation and Interpretation',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Collect and organise data into tables and graphs', 'Create bar graphs and pictographs', 'Interpret data displays to answer questions'],
          activities: ['Data collection surveys', 'Graph creation', 'Data interpretation'],
          resources: ['Graph paper', 'Data collection sheets', 'Interpretation worksheets']
        },
        {
          week: 7,
          topic: 'End-of-Year Revision - Number and Operations',
          strand: 'Number & Operations',
          learningOutcomes: ['Review all four operations with whole numbers', 'Consolidate fraction and decimal skills', 'Solve mixed number problems'],
          activities: ['Operations revision', 'Fraction/decimal review', 'Mixed practice'],
          resources: ['Revision booklets', 'Practice tests', 'Self-check materials']
        },
        {
          week: 8,
          topic: 'End-of-Year Revision - Measurement and Geometry',
          strand: 'Measurement',
          learningOutcomes: ['Review all measurement topics (length, area, capacity, mass, time, money)', 'Consolidate geometry knowledge (shapes, angles, coordinates)', 'Apply skills to practical problems'],
          activities: ['Measurement revision', 'Geometry review', 'Practical applications'],
          resources: ['Revision worksheets', 'Measuring equipment', 'Practice problems']
        },
        {
          week: 9,
          topic: 'End-of-Year Revision - Patterns, Chance and Data',
          strand: 'All Strands',
          learningOutcomes: ['Review patterns and problem solving', 'Consolidate chance and data skills', 'Prepare for end-of-year assessment'],
          activities: ['Patterns review', 'Data skills revision', 'Assessment preparation'],
          resources: ['Revision materials', 'Practice tests', 'Study guides']
        },
        {
          week: 10,
          topic: 'End-of-Year Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate mastery of Year 4 Mathematics', 'Show understanding across all strands', 'Apply problem solving to unfamiliar contexts'],
          activities: ['End-of-year examination', 'Practical assessment', 'Portfolio finalisation'],
          resources: ['Examination papers', 'Practical materials', 'Portfolio folders']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 5 MATHEMATICS CURRICULUM
// Pre-Algebra & Advanced Concepts
// ============================================
export const mathYear5 = {
  year: 5,
  subject: 'Mathematics',
  totalWeeks: 40,
  periodsPerWeek: 6,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Large Numbers & Decimal Operations',
      scheme: [
        {
          week: 1,
          topic: 'Place Value to Millions',
          strand: 'Number & Operations',
          learningOutcomes: ['Read/write numbers to millions', 'Compare large numbers', 'Round to any place'],
          activities: ['Place value extension', 'Comparison activities', 'Rounding practice'],
          resources: ['Place value charts', 'Number cards', 'Rounding activities']
        },
        {
          week: 2,
          topic: 'Operations with Large Numbers',
          strand: 'Number & Operations',
          learningOutcomes: ['Add/subtract large numbers', 'Multiply multi-digit numbers', 'Estimate answers'],
          activities: ['Multi-digit operations', 'Estimation practice', 'Problem solving'],
          resources: ['Worksheets', 'Estimation activities', 'Word problems']
        },
        {
          week: 3,
          topic: 'Decimal Place Value',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand decimal place value to thousandths', 'Read and write decimals', 'Compare and order'],
          activities: ['Place value exploration', 'Reading/writing practice', 'Ordering activities'],
          resources: ['Decimal models', 'Place value charts', 'Comparison materials']
        },
        {
          week: 4,
          topic: 'Multiplying Decimals',
          strand: 'Number & Operations',
          learningOutcomes: ['Multiply decimals by whole numbers', 'Multiply decimals by decimals', 'Place decimal correctly'],
          activities: ['Multiplication practice', 'Decimal placement', 'Problem solving'],
          resources: ['Decimal worksheets', 'Area models', 'Problems']
        },
        {
          week: 5,
          topic: 'Dividing Decimals',
          strand: 'Number & Operations',
          learningOutcomes: ['Divide decimals by whole numbers', 'Divide by decimals', 'Round quotients'],
          activities: ['Division practice', 'Rounding quotients', 'Application'],
          resources: ['Division materials', 'Practice sheets', 'Word problems']
        },
        {
          week: 6,
          topic: 'Long Division',
          strand: 'Number & Operations',
          learningOutcomes: ['Divide by two-digit divisors', 'Use estimation to divide', 'Check with multiplication'],
          activities: ['Long division practice', 'Estimation use', 'Checking work'],
          resources: ['Division worksheets', 'Estimation guides', 'Practice problems']
        },
        {
          week: 7,
          topic: 'Order of Operations (BODMAS)',
          strand: 'Number & Operations',
          learningOutcomes: ['Apply BODMAS rules', 'Evaluate expressions', 'Use parentheses'],
          activities: ['BODMAS practice', 'Expression evaluation', 'Problem solving'],
          resources: ['BODMAS charts', 'Expression cards', 'Practice problems']
        },
        {
          week: 8,
          topic: 'Exponents Introduction',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand exponent notation', 'Calculate powers of 10', 'Apply exponents'],
          activities: ['Exponent introduction', 'Powers of 10', 'Application'],
          resources: ['Exponent materials', 'Pattern exploration', 'Practice']
        },
        {
          week: 9,
          topic: 'Prime and Composite Numbers',
          strand: 'Number & Operations',
          learningOutcomes: ['Identify prime numbers', 'Find prime factorization', 'Use factor trees'],
          activities: ['Prime identification', 'Factorization practice', 'Factor trees'],
          resources: ['Prime charts', 'Factor tree templates', 'Practice problems']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate decimal operation skills', 'Show number knowledge', 'Apply problem solving'],
          activities: ['Assessment tasks', 'Skills review', 'Progress evaluation'],
          resources: ['Assessment materials', 'Review activities', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Fractions & Ratios',
      scheme: [
        {
          week: 1,
          topic: 'Fraction Operations Review',
          strand: 'Number & Operations',
          learningOutcomes: ['Add/subtract fractions with unlike denominators', 'Find common denominators', 'Simplify answers'],
          activities: ['Operation review', 'Common denominator practice', 'Simplification'],
          resources: ['Fraction materials', 'LCD guides', 'Practice problems']
        },
        {
          week: 2,
          topic: 'Multiplying Fractions',
          strand: 'Number & Operations',
          learningOutcomes: ['Multiply fractions', 'Multiply mixed numbers', 'Solve word problems'],
          activities: ['Multiplication practice', 'Mixed number work', 'Problem solving'],
          resources: ['Fraction models', 'Worksheets', 'Word problems']
        },
        {
          week: 3,
          topic: 'Dividing Fractions',
          strand: 'Number & Operations',
          learningOutcomes: ['Divide fractions', 'Use reciprocals', 'Solve division problems'],
          activities: ['Division introduction', 'Reciprocal practice', 'Problem solving'],
          resources: ['Fraction materials', 'Division guides', 'Problems']
        },
        {
          week: 4,
          topic: 'Ratios Introduction',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand ratio concept', 'Write ratios', 'Find equivalent ratios'],
          activities: ['Ratio introduction', 'Writing practice', 'Equivalence'],
          resources: ['Ratio materials', 'Examples', 'Practice problems']
        },
        {
          week: 5,
          topic: 'Proportions',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand proportions', 'Solve proportions', 'Apply to real situations'],
          activities: ['Proportion concept', 'Solving practice', 'Real-world application'],
          resources: ['Proportion materials', 'Practice problems', 'Application scenarios']
        },
        {
          week: 6,
          topic: 'Percentages Introduction',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand percent concept', 'Convert between percent, decimal, fraction', 'Calculate percentages'],
          activities: ['Percent introduction', 'Conversion practice', 'Calculation'],
          resources: ['Percent materials', 'Conversion charts', 'Practice problems']
        },
        {
          week: 7,
          topic: 'Percent Applications',
          strand: 'Number & Operations',
          learningOutcomes: ['Calculate percent of a number', 'Find discounts and sales tax', 'Solve percent problems'],
          activities: ['Percent calculations', 'Real-world applications', 'Problem solving'],
          resources: ['Application scenarios', 'Worksheets', 'Word problems']
        },
        {
          week: 8,
          topic: 'Rates and Unit Rates',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand rates', 'Calculate unit rates', 'Compare using rates'],
          activities: ['Rate introduction', 'Unit rate calculation', 'Comparison'],
          resources: ['Rate examples', 'Practice problems', 'Comparison activities']
        },
        {
          week: 9,
          topic: 'Fraction, Decimal, Percent Relationships',
          strand: 'Number & Operations',
          learningOutcomes: ['Convert fluently between forms', 'Compare different forms', 'Apply to problems'],
          activities: ['Conversion practice', 'Comparison activities', 'Application'],
          resources: ['Conversion charts', 'Comparison tools', 'Practice problems']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate fraction skills', 'Show ratio/proportion understanding', 'Apply percent concepts'],
          activities: ['Assessment tasks', 'Skills demonstration', 'Portfolio review'],
          resources: ['Assessment materials', 'Portfolios', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Geometry & Algebra',
      scheme: [
        {
          week: 1,
          topic: 'Coordinate Geometry',
          strand: 'Geometry',
          learningOutcomes: ['Plot points in four quadrants', 'Identify coordinates', 'Graph simple equations'],
          activities: ['Four quadrant plotting', 'Coordinate identification', 'Equation graphing'],
          resources: ['Coordinate grids', 'Plotting activities', 'Simple equations']
        },
        {
          week: 2,
          topic: 'Angle Relationships',
          strand: 'Geometry',
          learningOutcomes: ['Identify complementary angles', 'Identify supplementary angles', 'Find unknown angles'],
          activities: ['Relationship discovery', 'Angle calculation', 'Problem solving'],
          resources: ['Angle materials', 'Protractors', 'Practice problems']
        },
        {
          week: 3,
          topic: 'Triangles: Angle Sum',
          strand: 'Geometry',
          learningOutcomes: ['Understand angle sum property', 'Calculate missing angles', 'Apply to problems'],
          activities: ['Property discovery', 'Calculation practice', 'Problem solving'],
          resources: ['Triangle materials', 'Calculation worksheets', 'Problems']
        },
        {
          week: 4,
          topic: 'Area of Triangles',
          strand: 'Measurement',
          learningOutcomes: ['Apply area formula', 'Find area of various triangles', 'Solve area problems'],
          activities: ['Formula introduction', 'Calculation practice', 'Problem solving'],
          resources: ['Triangle shapes', 'Formula guides', 'Practice problems']
        },
        {
          week: 5,
          topic: 'Area of Parallelograms and Trapezoids',
          strand: 'Measurement',
          learningOutcomes: ['Calculate parallelogram area', 'Calculate trapezoid area', 'Apply formulas'],
          activities: ['Formula development', 'Calculation practice', 'Application'],
          resources: ['Shape materials', 'Formula guides', 'Practice problems']
        },
        {
          week: 6,
          topic: 'Volume of Rectangular Prisms',
          strand: 'Measurement',
          learningOutcomes: ['Understand volume concept', 'Calculate volume', 'Solve volume problems'],
          activities: ['Volume exploration', 'Calculation practice', 'Problem solving'],
          resources: ['Cubes', 'Volume worksheets', 'Problems']
        },
        {
          week: 7,
          topic: 'Algebraic Expressions',
          strand: 'Number & Operations',
          learningOutcomes: ['Write algebraic expressions', 'Evaluate expressions', 'Simplify expressions'],
          activities: ['Expression writing', 'Evaluation practice', 'Simplification'],
          resources: ['Expression cards', 'Evaluation worksheets', 'Practice problems']
        },
        {
          week: 8,
          topic: 'Solving Simple Equations',
          strand: 'Number & Operations',
          learningOutcomes: ['Solve one-step equations', 'Use inverse operations', 'Check solutions'],
          activities: ['Equation solving', 'Inverse operation use', 'Solution checking'],
          resources: ['Equation materials', 'Practice problems', 'Checking guides']
        },
        {
          week: 9,
          topic: 'Inequalities',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand inequality symbols', 'Write inequalities', 'Graph on number line'],
          activities: ['Symbol introduction', 'Writing practice', 'Number line graphing'],
          resources: ['Inequality materials', 'Number lines', 'Practice problems']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate geometry skills', 'Show area/volume understanding', 'Apply algebra concepts'],
          activities: ['Assessment tasks', 'Skills review', 'Progress check'],
          resources: ['Assessment materials', 'Review activities', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Statistics & Problem Solving',
      scheme: [
        {
          week: 1,
          topic: 'Data Analysis',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Collect and organize data', 'Calculate mean, median, mode', 'Interpret statistics'],
          activities: ['Data collection', 'Statistical calculation', 'Interpretation'],
          resources: ['Data sets', 'Calculation guides', 'Interpretation activities']
        },
        {
          week: 2,
          topic: 'Graphs: Line Graphs',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Create line graphs', 'Interpret trends', 'Make predictions'],
          activities: ['Graph creation', 'Trend analysis', 'Prediction practice'],
          resources: ['Graph paper', 'Data sets', 'Prediction worksheets']
        },
        {
          week: 3,
          topic: 'Circle Graphs',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Read circle graphs', 'Create simple circle graphs', 'Compare data'],
          activities: ['Graph reading', 'Graph creation', 'Data comparison'],
          resources: ['Circle graph examples', 'Graph materials', 'Comparison activities']
        },
        {
          week: 4,
          topic: 'Probability',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Calculate simple probability', 'Express as fraction/percent', 'Conduct experiments'],
          activities: ['Probability calculation', 'Expression practice', 'Experiments'],
          resources: ['Probability materials', 'Experiment supplies', 'Recording sheets']
        },
        {
          week: 5,
          topic: 'Problem Solving Strategies',
          strand: 'All Strands',
          learningOutcomes: ['Apply multiple strategies', 'Choose best strategy', 'Explain reasoning'],
          activities: ['Strategy practice', 'Strategy selection', 'Explanation'],
          resources: ['Problem sets', 'Strategy guides', 'Discussion materials']
        },
        {
          week: 6,
          topic: 'Surface Area Introduction',
          strand: 'Measurement',
          learningOutcomes: ['Understand surface area concept', 'Find surface area of rectangular prisms', 'Apply to problems'],
          activities: ['Concept introduction', 'Calculation practice', 'Problem solving'],
          resources: ['3D models', 'Calculation guides', 'Problems']
        },
        {
          week: 7,
          topic: 'Review: Number Operations',
          strand: 'Number & Operations',
          learningOutcomes: ['Review all operations', 'Apply fluently', 'Solve complex problems'],
          activities: ['Operations review', 'Fluency practice', 'Complex problems'],
          resources: ['Review materials', 'Practice problems', 'Games']
        },
        {
          week: 8,
          topic: 'Review: Geometry and Measurement',
          strand: 'Geometry/Measurement',
          learningOutcomes: ['Review geometry concepts', 'Review measurement skills', 'Apply to problems'],
          activities: ['Geometry review', 'Measurement review', 'Application'],
          resources: ['Review materials', 'Measuring tools', 'Shape materials']
        },
        {
          week: 9,
          topic: 'Year 5 Review',
          strand: 'All Strands',
          learningOutcomes: ['Comprehensive Year 5 review', 'Identify mastered skills', 'Prepare for Year 6'],
          activities: ['Full review', 'Skills assessment', 'Goal setting'],
          resources: ['Review materials', 'Assessment tools', 'Goal worksheets']
        },
        {
          week: 10,
          topic: 'Year 5 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Complete final assessment', 'Celebrate achievements', 'Transition to Year 6'],
          activities: ['Final testing', 'Awards ceremony', 'Celebration'],
          resources: ['Assessment materials', 'Certificates', 'Celebration supplies']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 6 MATHEMATICS CURRICULUM
// Secondary Preparation
// ============================================
export const mathYear6 = {
  year: 6,
  subject: 'Mathematics',
  totalWeeks: 40,
  periodsPerWeek: 7,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Advanced Operations & Algebra',
      scheme: [
        {
          week: 1,
          topic: 'Integers Introduction',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand negative numbers', 'Compare and order integers', 'Plot on number line'],
          activities: ['Integer introduction', 'Comparison activities', 'Number line plotting'],
          resources: ['Integer materials', 'Number lines', 'Comparison cards']
        },
        {
          week: 2,
          topic: 'Operations with Integers',
          strand: 'Number & Operations',
          learningOutcomes: ['Add integers', 'Subtract integers', 'Apply integer rules'],
          activities: ['Addition practice', 'Subtraction practice', 'Rule application'],
          resources: ['Integer counters', 'Number lines', 'Practice problems']
        },
        {
          week: 3,
          topic: 'Advanced Decimal Operations',
          strand: 'Number & Operations',
          learningOutcomes: ['Perform all operations with decimals', 'Solve multi-step problems', 'Apply to real situations'],
          activities: ['Mixed operations', 'Problem solving', 'Real-world application'],
          resources: ['Decimal worksheets', 'Multi-step problems', 'Application scenarios']
        },
        {
          week: 4,
          topic: 'Advanced Fraction Operations',
          strand: 'Number & Operations',
          learningOutcomes: ['Perform all operations with fractions', 'Work with mixed numbers', 'Solve complex problems'],
          activities: ['Mixed operations', 'Complex problems', 'Application'],
          resources: ['Fraction materials', 'Practice problems', 'Word problems']
        },
        {
          week: 5,
          topic: 'Algebraic Expressions and Equations',
          strand: 'Number & Operations',
          learningOutcomes: ['Write complex expressions', 'Solve two-step equations', 'Apply to problems'],
          activities: ['Expression writing', 'Equation solving', 'Application'],
          resources: ['Expression/equation materials', 'Practice problems', 'Word problems']
        },
        {
          week: 6,
          topic: 'Graphing Linear Equations',
          strand: 'Number & Operations',
          learningOutcomes: ['Create tables for equations', 'Graph linear equations', 'Interpret graphs'],
          activities: ['Table creation', 'Graphing practice', 'Interpretation'],
          resources: ['Graphing grids', 'Equation cards', 'Interpretation guides']
        },
        {
          week: 7,
          topic: 'Ratios, Proportions, and Percent Review',
          strand: 'Number & Operations',
          learningOutcomes: ['Apply ratio concepts', 'Solve proportion problems', 'Calculate percentages'],
          activities: ['Concept application', 'Problem solving', 'Percent calculation'],
          resources: ['Review materials', 'Practice problems', 'Application scenarios']
        },
        {
          week: 8,
          topic: 'Scientific Notation Introduction',
          strand: 'Number & Operations',
          learningOutcomes: ['Understand scientific notation', 'Convert to/from standard form', 'Use for large/small numbers'],
          activities: ['Notation introduction', 'Conversion practice', 'Application'],
          resources: ['Notation materials', 'Conversion guides', 'Practice problems']
        },
        {
          week: 9,
          topic: 'Order of Operations Review',
          strand: 'Number & Operations',
          learningOutcomes: ['Apply BODMAS to complex expressions', 'Evaluate nested parentheses', 'Solve complex calculations'],
          activities: ['Complex evaluation', 'Nested expressions', 'Problem solving'],
          resources: ['Expression cards', 'Practice problems', 'Calculation challenges']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate advanced number skills', 'Show algebra understanding', 'Apply problem solving'],
          activities: ['Assessment tasks', 'Skills review', 'Progress evaluation'],
          resources: ['Assessment materials', 'Review activities', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Advanced Geometry & Measurement',
      scheme: [
        {
          week: 1,
          topic: 'Circles: Parts and Circumference',
          strand: 'Geometry',
          learningOutcomes: ['Identify circle parts', 'Calculate circumference', 'Apply pi'],
          activities: ['Part identification', 'Circumference calculation', 'Pi application'],
          resources: ['Circle materials', 'Circumference guides', 'Practice problems']
        },
        {
          week: 2,
          topic: 'Area of Circles',
          strand: 'Measurement',
          learningOutcomes: ['Calculate circle area', 'Apply formula', 'Solve area problems'],
          activities: ['Formula introduction', 'Calculation practice', 'Problem solving'],
          resources: ['Circle materials', 'Area guides', 'Practice problems']
        },
        {
          week: 3,
          topic: 'Composite Shapes',
          strand: 'Measurement',
          learningOutcomes: ['Find area of composite shapes', 'Find perimeter of composite shapes', 'Solve complex problems'],
          activities: ['Composite area', 'Composite perimeter', 'Problem solving'],
          resources: ['Shape cards', 'Worksheets', 'Complex problems']
        },
        {
          week: 4,
          topic: 'Surface Area',
          strand: 'Measurement',
          learningOutcomes: ['Calculate surface area of prisms', 'Apply to various shapes', 'Solve problems'],
          activities: ['Formula application', 'Various shapes', 'Problem solving'],
          resources: ['3D models', 'Formula guides', 'Practice problems']
        },
        {
          week: 5,
          topic: 'Volume of Prisms and Cylinders',
          strand: 'Measurement',
          learningOutcomes: ['Calculate volume of various prisms', 'Calculate cylinder volume', 'Apply formulas'],
          activities: ['Volume calculation', 'Formula application', 'Problem solving'],
          resources: ['3D models', 'Formula guides', 'Practice problems']
        },
        {
          week: 6,
          topic: 'Transformations: Reflection and Rotation',
          strand: 'Geometry',
          learningOutcomes: ['Perform reflections', 'Perform rotations', 'Identify transformation properties'],
          activities: ['Reflection practice', 'Rotation practice', 'Property identification'],
          resources: ['Transformation materials', 'Grid paper', 'Practice activities']
        },
        {
          week: 7,
          topic: 'Transformations: Translation and Dilation',
          strand: 'Geometry',
          learningOutcomes: ['Perform translations', 'Understand dilation', 'Combine transformations'],
          activities: ['Translation practice', 'Dilation introduction', 'Combined transformations'],
          resources: ['Grid paper', 'Transformation guides', 'Practice activities']
        },
        {
          week: 8,
          topic: 'Pythagorean Theorem Introduction',
          strand: 'Geometry',
          learningOutcomes: ['Understand theorem concept', 'Apply to right triangles', 'Calculate missing sides'],
          activities: ['Theorem introduction', 'Application practice', 'Side calculation'],
          resources: ['Right triangle materials', 'Theorem guides', 'Practice problems']
        },
        {
          week: 9,
          topic: 'Similar and Congruent Figures',
          strand: 'Geometry',
          learningOutcomes: ['Identify similar figures', 'Identify congruent figures', 'Find missing measures'],
          activities: ['Figure identification', 'Property exploration', 'Measure finding'],
          resources: ['Shape materials', 'Comparison activities', 'Practice problems']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate geometry skills', 'Show measurement mastery', 'Apply concepts'],
          activities: ['Assessment tasks', 'Skills demonstration', 'Portfolio review'],
          resources: ['Assessment materials', 'Portfolios', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Statistics & Advanced Problem Solving',
      scheme: [
        {
          week: 1,
          topic: 'Data Collection and Sampling',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Design surveys', 'Understand sampling', 'Collect representative data'],
          activities: ['Survey design', 'Sampling practice', 'Data collection'],
          resources: ['Survey materials', 'Sampling guides', 'Recording sheets']
        },
        {
          week: 2,
          topic: 'Measures of Central Tendency',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Calculate mean, median, mode', 'Choose appropriate measure', 'Interpret measures'],
          activities: ['Calculation practice', 'Measure selection', 'Interpretation'],
          resources: ['Data sets', 'Calculation guides', 'Interpretation activities']
        },
        {
          week: 3,
          topic: 'Data Displays',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Create various graphs', 'Choose appropriate display', 'Interpret displays'],
          activities: ['Graph creation', 'Display selection', 'Interpretation'],
          resources: ['Graph materials', 'Data sets', 'Display options']
        },
        {
          week: 4,
          topic: 'Probability',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Calculate theoretical probability', 'Conduct experiments', 'Compare theoretical and experimental'],
          activities: ['Probability calculation', 'Experiments', 'Comparison'],
          resources: ['Probability materials', 'Experiment supplies', 'Recording sheets']
        },
        {
          week: 5,
          topic: 'Compound Events',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Understand compound events', 'Use tree diagrams', 'Calculate compound probability'],
          activities: ['Compound event introduction', 'Tree diagram creation', 'Calculation'],
          resources: ['Event materials', 'Tree diagram templates', 'Practice problems']
        },
        {
          week: 6,
          topic: 'Multi-Step Problem Solving',
          strand: 'All Strands',
          learningOutcomes: ['Analyze complex problems', 'Plan multi-step solutions', 'Verify answers'],
          activities: ['Problem analysis', 'Solution planning', 'Verification'],
          resources: ['Complex problems', 'Planning guides', 'Verification tools']
        },
        {
          week: 7,
          topic: 'Financial Mathematics',
          strand: 'Number & Operations',
          learningOutcomes: ['Calculate simple interest', 'Understand budgets', 'Solve financial problems'],
          activities: ['Interest calculation', 'Budget practice', 'Problem solving'],
          resources: ['Financial materials', 'Budget templates', 'Problems']
        },
        {
          week: 8,
          topic: 'Patterns and Functions',
          strand: 'Number & Operations',
          learningOutcomes: ['Identify function rules', 'Create input-output tables', 'Graph functions'],
          activities: ['Rule identification', 'Table creation', 'Graphing'],
          resources: ['Pattern materials', 'Function tables', 'Graphing grids']
        },
        {
          week: 9,
          topic: 'Review: Algebra and Functions',
          strand: 'Number & Operations',
          learningOutcomes: ['Review algebraic concepts', 'Apply function understanding', 'Solve complex problems'],
          activities: ['Concept review', 'Application practice', 'Problem solving'],
          resources: ['Review materials', 'Practice problems', 'Games']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate statistics skills', 'Show problem solving ability', 'Apply financial concepts'],
          activities: ['Assessment tasks', 'Skills review', 'Progress check'],
          resources: ['Assessment materials', 'Review activities', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Secondary Preparation & Consolidation',
      scheme: [
        {
          week: 1,
          topic: 'Number System Review',
          strand: 'Number & Operations',
          learningOutcomes: ['Review all number types', 'Convert between forms', 'Apply to problems'],
          activities: ['Number type review', 'Conversion practice', 'Problem solving'],
          resources: ['Review materials', 'Conversion charts', 'Practice problems']
        },
        {
          week: 2,
          topic: 'Operations Review',
          strand: 'Number & Operations',
          learningOutcomes: ['Apply all operations fluently', 'Solve complex calculations', 'Estimate accurately'],
          activities: ['Operations practice', 'Complex calculations', 'Estimation'],
          resources: ['Practice materials', 'Complex problems', 'Estimation activities']
        },
        {
          week: 3,
          topic: 'Algebra Review',
          strand: 'Number & Operations',
          learningOutcomes: ['Review expressions and equations', 'Solve various equation types', 'Apply algebraic thinking'],
          activities: ['Expression/equation review', 'Solving practice', 'Application'],
          resources: ['Review materials', 'Practice problems', 'Application scenarios']
        },
        {
          week: 4,
          topic: 'Geometry Review',
          strand: 'Geometry',
          learningOutcomes: ['Review all geometry concepts', 'Apply properties', 'Solve geometry problems'],
          activities: ['Concept review', 'Property application', 'Problem solving'],
          resources: ['Shape materials', 'Review guides', 'Practice problems']
        },
        {
          week: 5,
          topic: 'Measurement Review',
          strand: 'Measurement',
          learningOutcomes: ['Review all measurement concepts', 'Convert units fluently', 'Solve measurement problems'],
          activities: ['Concept review', 'Conversion practice', 'Problem solving'],
          resources: ['Measurement tools', 'Conversion charts', 'Practice problems']
        },
        {
          week: 6,
          topic: 'Statistics and Probability Review',
          strand: 'Statistics & Probability',
          learningOutcomes: ['Review data analysis', 'Review probability', 'Apply to real situations'],
          activities: ['Data review', 'Probability review', 'Real-world application'],
          resources: ['Review materials', 'Data sets', 'Application scenarios']
        },
        {
          week: 7,
          topic: 'Secondary Mathematics Preview',
          strand: 'All Strands',
          learningOutcomes: ['Preview Year 7 topics', 'Identify readiness', 'Set learning goals'],
          activities: ['Topic preview', 'Readiness assessment', 'Goal setting'],
          resources: ['Preview materials', 'Assessment tools', 'Goal worksheets']
        },
        {
          week: 8,
          topic: 'Problem Solving Mastery',
          strand: 'All Strands',
          learningOutcomes: ['Apply all strategies', 'Solve challenging problems', 'Explain solutions'],
          activities: ['Strategy application', 'Challenge problems', 'Solution explanation'],
          resources: ['Challenging problems', 'Strategy guides', 'Explanation frames']
        },
        {
          week: 9,
          topic: 'Year 6 Comprehensive Review',
          strand: 'All Strands',
          learningOutcomes: ['Review all Year 6 concepts', 'Demonstrate mastery', 'Prepare for final assessment'],
          activities: ['Comprehensive review', 'Mastery demonstration', 'Final preparation'],
          resources: ['Review materials', 'Demonstration tasks', 'Preparation guides']
        },
        {
          week: 10,
          topic: 'Year 6 Final Assessment & Graduation',
          strand: 'All Strands',
          learningOutcomes: ['Complete final assessment', 'Celebrate primary completion', 'Transition to secondary'],
          activities: ['Final testing', 'Graduation ceremony', 'Celebration'],
          resources: ['Assessment materials', 'Certificates', 'Celebration supplies']
        }
      ]
    }
  ]
};

export default {
  mathYear1,
  mathYear2,
  mathYear3,
  mathYear4,
  mathYear5,
  mathYear6
};
